import { action, computed, observable } from "mobx";
import { ObservableSubscription } from "@apollo/client";
import { debounce } from "lodash";
import { BaseStore } from "@store/BaseStore";
import { store } from "@store";
import {
  BookingEventLog_AddComment as addComment,
  BookingEventLog_Delete as deleteEvent,
  BookingEventLog_GetEvents as getEvents,
  BookingEventLog_Update as updateEvent,
  BookingEventType,
  EventLog_BookingEventFragment as Event
} from "@graphql";
import { CommentBoxData } from "../CommentBox/CommentBoxStore";
import { ReminderFormData } from "@containers/ReminderDialog/ReminderStore";

export class BookingEventLogStore extends BaseStore {
  @computed get events() {
    return this._events?.filter((ev) => !ev.Deleted) ?? [];
  }

  @observable eventTypeFilter: BookingEventType[] = [];

  private query = getEvents.funcWatch(
    { bookingId: this.bookingId },
    {
      fetchPolicy: "network-only"
    }
  );

  private queryVars: getEvents.Variables;
  private subscription: ObservableSubscription;

  @observable private _events: Event[] = [];

  private refetch = debounce(() => this.query.refetch(this.queryVars), 200);

  constructor(private bookingId: string) {
    super();
    this.subscribe();
  }

  subscribe() {
    const { snackbars } = store();

    this.subscription = this.query.subscribe({
      next: ({ data, loading }) => {
        if (loading) {
          this.setLoading();
        }

        this._events = data.bookingEvents;

        this.setReady();
      },

      error: (e) => snackbars.error(`Cannot get events\nReason: ${e}`)
    });
  }

  unsubscribe() {
    this.subscription && this.subscription.unsubscribe();
  }

  @action.bound addComment(data: CommentBoxData, reminder?: ReminderFormData) {
    return addComment
      .func({
        bookingId: this.bookingId,
        desc: data.text
      })
      .then(() => this.refetch())
      .catch((e) => store().snackbars.error(`Error occurred: ${e}`));
  }

  @action.bound setEventTypeFilter(value: []) {
    this.eventTypeFilter = value;
  }

  updateEvent = (eventId: string, desc: string) => {
    return updateEvent
      .func({ eventId, desc })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot update event\nReason: ${err}`);
      });
  };

  deleteEvent = (eventId: string) => {
    return deleteEvent
      .func({ eventId })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot delete event\nReason: ${err}`);
      });
  };

  handlePin = (eventId: string, sticky: boolean) => {
    return updateEvent
      .func({
        eventId,
        sticky
      })
      .then(() => this.refetch())
      .catch((err) => {
        store().snackbars.error(`Cannot update event\nReason: ${err}`);
      });
  };
}
